import { type ReactNode, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import posthog from "@/lib/posthog";
import { trpc } from "@/trpc";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  LANE_BY_PROGRESS,
  LANES,
  temperatureBarClasses,
  type Lane,
  type LaneProgressId,
} from "../progress";
import { useInactivoSelection } from "../InactivoSelectionContext";
import StickyScrollRail from "../StickyScrollRail";
import type { InactivoLead } from "../types";
import InactivoCard from "./InactivoCard";

const PAGE_SIZE = 25;

export type FunnelFilters = {
  startDate?: string;
  endDate?: string;
  source?: string;
  leadType?: string;
  campaignName?: string;
  tagIds?: string[];
  // A phone number or a WhatsApp handle, classified by the backend. When set,
  // the board swaps the per-lane paginated queries for a single search call
  // (see SearchLanes).
  search?: string;
};

const funnelFiltersKey = (filters: FunnelFilters): string =>
  [
    filters.startDate,
    filters.endDate,
    filters.source,
    filters.leadType,
    filters.campaignName,
    filters.tagIds?.join(","),
  ].join("|");

const LaneSelectionControls = ({
  scope,
  filters,
  merchantId,
  total,
}: {
  scope: Lane;
  filters: FunnelFilters;
  merchantId: string;
  total: number;
}) => {
  const selection = useInactivoSelection();
  const utils = trpc.useUtils();
  const [loadingAll, setLoadingAll] = useState(false);
  if (!selection.enabled) return null;

  // Controls only appear once the user has selected at least one card in this
  // lane — the checkbox itself is what enters selection mode.
  const active = selection.isActive(scope.id);
  if (!active || selection.ids.size === 0) return null;

  const handleSelectAll = async () => {
    setLoadingAll(true);
    try {
      const result = await utils.client.leads.listInactiveIdsByProgress.query({
        merchantId,
        assessmentProgresses: scope.progressIds,
        startDate: filters.startDate,
        endDate: filters.endDate,
        source: filters.source,
        leadType: filters.leadType,
        campaignName: filters.campaignName,
        tagIds: filters.tagIds,
        limit: 500,
      });
      selection.selectAllInLane(scope, result.ids, result.capped);
      posthog.capture("tablero select all clicked", {
        merchant_id: merchantId,
        lane_id: scope.id,
        returned_count: result.ids.length,
        cap_hit: result.capped,
      });
    } catch (err) {
      console.error(err);
      toast.error("No pudimos cargar todos los leads de esta etapa");
    } finally {
      setLoadingAll(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-neutral-300 bg-surface px-2 py-2 text-xs">
      <span className="font-medium text-neutral-900">{selection.ids.size} seleccionados</span>
      {selection.ids.size < total ? (
        <button
          type="button"
          disabled={loadingAll}
          onClick={handleSelectAll}
          className="inline-flex min-h-9 items-center rounded-md border border-palomma-primary/40 bg-palomma-primary/5 px-2 py-1 font-medium text-palomma-primary hover:bg-palomma-primary/10 disabled:opacity-50"
        >
          {loadingAll ? "Cargando…" : `Seleccionar todos los ${total}`}
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => selection.clear()}
        className="inline-flex min-h-9 items-center rounded-md px-2 py-1 font-medium text-neutral-600 hover:text-neutral-900"
      >
        Cancelar
      </button>
    </div>
  );
};

const LaneColumna = ({
  lane,
  total,
  isLoading,
  hasMore,
  loadingMore,
  onLoadMore,
  headerAction,
  isEmpty,
  emptyLabel = "Sin leads",
  children,
}: {
  lane: Lane;
  total: number;
  isLoading: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  headerAction?: ReactNode;
  isEmpty: boolean;
  emptyLabel?: string;
  children: ReactNode;
}) => (
  <div className="flex w-[280px] shrink-0 flex-col rounded-md bg-neutral-100/50">
    <div className={cn("h-1 rounded-t-md", temperatureBarClasses(lane.temperature))} />
    <div className="flex h-10 items-center justify-between px-3">
      <span className="truncate text-xs font-semibold text-neutral-900">{lane.label}</span>
      <span className="rounded-pill bg-white/60 px-2 py-0.5 text-xs font-semibold tabular-nums text-neutral-900">
        {total}
      </span>
    </div>
    {headerAction}
    <div className="min-h-[200px] flex-1 space-y-2 overflow-y-auto p-2">
      {children}
      {isLoading ? (
        <div className="py-6 text-center text-xs text-neutral-600">Cargando…</div>
      ) : isEmpty ? (
        <div className="py-6 text-center text-xs text-neutral-600">{emptyLabel}</div>
      ) : null}
      {hasMore ? (
        <button
          onClick={onLoadMore}
          disabled={loadingMore}
          className="min-h-11 w-full rounded-md border border-neutral-300 bg-surface text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-100 disabled:opacity-50"
        >
          {loadingMore ? "Cargando…" : "Cargar más"}
        </button>
      ) : null}
    </div>
  </div>
);

const LaneCards = ({
  leads,
  lane,
  showProgressTag,
  onSelectLead,
}: {
  leads: InactivoLead[];
  lane: Lane;
  showProgressTag?: boolean;
  onSelectLead: (leadId: string) => void;
}) => {
  const selection = useInactivoSelection();
  return (
    <>
      {leads.map((lead) => (
        <InactivoCard
          key={lead.id}
          lead={lead}
          selectable={selection.enabled}
          selected={selection.ids.has(lead.id)}
          showProgressTag={showProgressTag}
          onToggle={() => selection.toggle(lead.id, lane)}
          onOpen={() => onSelectLead(lead.id)}
        />
      ))}
    </>
  );
};

// Single-progress lane: one tRPC query, manual page state.
const SingleProgressLane = ({
  merchantId,
  lane,
  filters,
  onSelectLead,
}: {
  merchantId: string;
  lane: Lane;
  filters: FunnelFilters;
  onSelectLead: (leadId: string) => void;
}) => {
  const progressId = lane.progressIds[0]!;
  const [page, setPage] = useState(0);
  const [accumulated, setAccumulated] = useState<InactivoLead[]>([]);

  const filtersKey = funnelFiltersKey(filters);
  useEffect(() => {
    setPage(0);
    setAccumulated([]);
  }, [filtersKey]);

  const query = trpc.leads.listInactiveByProgress.useQuery({
    merchantId,
    assessmentProgress: progressId,
    startDate: filters.startDate,
    endDate: filters.endDate,
    source: filters.source,
    leadType: filters.leadType,
    campaignName: filters.campaignName,
    tagIds: filters.tagIds,
    page,
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    if (!query.data) return;
    if (page === 0) {
      setAccumulated(query.data.data as InactivoLead[]);
    } else {
      setAccumulated((prev) => [...prev, ...(query.data!.data as InactivoLead[])]);
    }
  }, [query.data, page]);

  const total = query.data?.total ?? accumulated.length;

  return (
    <LaneColumna
      lane={lane}
      total={total}
      isLoading={query.isLoading && accumulated.length === 0}
      hasMore={accumulated.length < total}
      loadingMore={query.isFetching}
      onLoadMore={() => setPage((p) => p + 1)}
      isEmpty={accumulated.length === 0}
      headerAction={
        <LaneSelectionControls
          scope={lane}
          filters={filters}
          merchantId={merchantId}
          total={total}
        />
      }
    >
      <LaneCards leads={accumulated} lane={lane} onSelectLead={onSelectLead} />
    </LaneColumna>
  );
};

// Cold lane (T=0): three progresses share one column. Three parallel queries
// merged by createdAt desc; "Cargar más" advances every sub-query in lockstep.
const ColdLane = ({
  merchantId,
  lane,
  filters,
  onSelectLead,
}: {
  merchantId: string;
  lane: Lane;
  filters: FunnelFilters;
  onSelectLead: (leadId: string) => void;
}) => {
  // Lane progressIds is a fixed 3-tuple — read by index so hook order is stable.
  const p0 = lane.progressIds[0]!;
  const p1 = lane.progressIds[1]!;
  const p2 = lane.progressIds[2]!;

  const [page, setPage] = useState(0);
  const [accumulated, setAccumulated] = useState<InactivoLead[]>([]);
  const filtersKey = funnelFiltersKey(filters);
  useEffect(() => {
    setPage(0);
    setAccumulated([]);
  }, [filtersKey]);

  const baseInput = {
    merchantId,
    startDate: filters.startDate,
    endDate: filters.endDate,
    source: filters.source,
    leadType: filters.leadType,
    campaignName: filters.campaignName,
    tagIds: filters.tagIds,
    page,
    pageSize: PAGE_SIZE,
  };

  const q0 = trpc.leads.listInactiveByProgress.useQuery({ ...baseInput, assessmentProgress: p0 });
  const q1 = trpc.leads.listInactiveByProgress.useQuery({ ...baseInput, assessmentProgress: p1 });
  const q2 = trpc.leads.listInactiveByProgress.useQuery({ ...baseInput, assessmentProgress: p2 });

  const allLoaded = !q0.isLoading && !q1.isLoading && !q2.isLoading;
  const someFetching = q0.isFetching || q1.isFetching || q2.isFetching;

  useEffect(() => {
    if (!allLoaded) return;
    const newRows = [
      ...((q0.data?.data ?? []) as InactivoLead[]),
      ...((q1.data?.data ?? []) as InactivoLead[]),
      ...((q2.data?.data ?? []) as InactivoLead[]),
    ].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

    if (page === 0) {
      setAccumulated(newRows);
    } else {
      setAccumulated((prev) =>
        [...prev, ...newRows].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q0.data, q1.data, q2.data, allLoaded, page]);

  const total = (q0.data?.total ?? 0) + (q1.data?.total ?? 0) + (q2.data?.total ?? 0);

  return (
    <LaneColumna
      lane={lane}
      total={total}
      isLoading={!allLoaded && accumulated.length === 0}
      hasMore={accumulated.length < total}
      loadingMore={someFetching}
      onLoadMore={() => setPage((p) => p + 1)}
      isEmpty={accumulated.length === 0}
      headerAction={
        <LaneSelectionControls
          scope={lane}
          filters={filters}
          merchantId={merchantId}
          total={total}
        />
      }
    >
      <LaneCards leads={accumulated} lane={lane} showProgressTag onSelectLead={onSelectLead} />
    </LaneColumna>
  );
};

// Search: one DynamoDB call spans every progress, so the board drops the
// per-lane paginated queries and groups a single bounded result set
// client-side. The term may be a phone or a WhatsApp handle — the backend picks
// the index — so a phoneless inactive lead is reachable here by its username.
// Read-only — the container turns selection off while a search is active,
// because "seleccionar todos" resolves ids from the unfiltered lane.
const SearchLanes = ({
  merchantId,
  filters,
  onSelectLead,
}: {
  merchantId: string;
  filters: FunnelFilters;
  onSelectLead: (leadId: string) => void;
}) => {
  const search = filters.search ?? "";
  const query = trpc.leads.searchInactive.useQuery(
    {
      merchantId,
      search,
      startDate: filters.startDate,
      endDate: filters.endDate,
      source: filters.source,
      leadType: filters.leadType,
      campaignName: filters.campaignName,
      limit: 200,
    },
    { enabled: search.length > 0 }
  );

  const tagIds = filters.tagIds;
  const byLane = useMemo(() => {
    const map = new Map<string, InactivoLead[]>();
    for (const lead of (query.data?.data ?? []) as InactivoLead[]) {
      if (tagIds?.length && !lead.tagIds.some((id) => tagIds.includes(id))) continue;
      // Manually-inactivated leads carry no assessment fields and come back
      // with a null progress — that's the "Sin evaluar" lane, same mapping
      // the paginated path gets from its own unassessed branch.
      const lane =
        LANE_BY_PROGRESS[(lead.assessmentProgress ?? "unassessed") as LaneProgressId];
      if (!lane) continue;
      const list = map.get(lane.id) ?? [];
      list.push(lead);
      map.set(lane.id, list);
    }
    return map;
  }, [query.data, tagIds]);

  return (
    <>
      {LANES.map((lane) => {
        const leads = byLane.get(lane.id) ?? [];
        return (
          <LaneColumna
            key={lane.id}
            lane={lane}
            total={leads.length}
            isLoading={query.isLoading}
            hasMore={false}
            loadingMore={false}
            onLoadMore={() => {}}
            isEmpty={leads.length === 0}
            emptyLabel="Sin coincidencias"
          >
            <LaneCards
              leads={leads}
              lane={lane}
              showProgressTag={lane.progressIds.length > 1}
              onSelectLead={onSelectLead}
            />
          </LaneColumna>
        );
      })}
    </>
  );
};

const ScopeSwitchDialog = () => {
  const selection = useInactivoSelection();
  const pending = selection.pendingScopeSwitch;
  return (
    <AlertDialog
      open={!!pending}
      onOpenChange={(open) => {
        if (!open && pending) pending.resolve(false);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cambiar de etapa</AlertDialogTitle>
          <AlertDialogDescription>
            {pending
              ? `Tienes ${selection.ids.size} leads seleccionados en ${pending.current.label}. Si cambias de etapa perderás esta selección.`
              : ""}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => pending?.resolve(false)}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={() => pending?.resolve(true)}>
            Cambiar de etapa
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const FunnelComercial = ({
  merchantId,
  filters,
  onSelectLead,
}: {
  merchantId: string;
  filters: FunnelFilters;
  onSelectLead: (leadId: string) => void;
}) => {
  const isSearch = !!filters.search;
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between gap-2 px-4 pt-3 text-[10px] font-semibold uppercase tracking-wider">
        <span className="text-neutral-600">Frío</span>
        <div className="mx-3 h-2 flex-1 rounded-pill bg-gradient-to-r from-neutral-300 via-warning to-danger" />
        <span className="text-danger">Caliente</span>
      </div>
      {isSearch ? (
        <p className="px-4 pt-2 text-xs text-neutral-600">
          Búsqueda por teléfono o usuario — limpia la búsqueda para volver al embudo completo.
        </p>
      ) : null}
      <StickyScrollRail className="flex flex-1 gap-3 p-4">
        {isSearch ? (
          <SearchLanes merchantId={merchantId} filters={filters} onSelectLead={onSelectLead} />
        ) : (
          LANES.map((lane) =>
            lane.progressIds.length > 1 ? (
              <ColdLane
                key={lane.id}
                merchantId={merchantId}
                lane={lane}
                filters={filters}
                onSelectLead={onSelectLead}
              />
            ) : (
              <SingleProgressLane
                key={lane.id}
                merchantId={merchantId}
                lane={lane}
                filters={filters}
                onSelectLead={onSelectLead}
              />
            )
          )
        )}
      </StickyScrollRail>
      <ScopeSwitchDialog />
    </div>
  );
};

export default FunnelComercial;
